import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import toast from 'react-hot-toast';
import { BLOOD_GROUPS, DISTRICTS_KERALA } from '../types/schema';

const BASE_URL = window.location.origin;

const PRESETS = [
  { label: 'General Registration', description: 'Open registration — anyone can join', params: {} },
  { label: 'Blood Camp / Drive', description: 'For use at a blood donation camp', params: { event: 'camp' } },
  { label: 'Parish Registration', description: 'Pre-tag registrations to a specific parish', params: { source: 'parish' } },
  { label: 'Walk-in at Hospital', description: 'Quick registration at hospital counters', params: { source: 'hospital' } },
];

export default function AdminQRGenerator({ currentAdmin }) {
  const canvasRef = useRef(null);
  const [config, setConfig] = useState({
    preset: 0,
    label: '',
    district: '',
    bloodGroup: '',
    parish: '',
    expiresIn: '7', // days, '' = never
    notes: '',
  });
  const [qrUrl, setQrUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [generated, setGenerated] = useState(false);

  const buildUrl = () => {
    const preset = PRESETS[config.preset];
    const params = new URLSearchParams({
      ...preset.params,
      ...(config.district && { district: config.district }),
      ...(config.bloodGroup && { bloodGroup: config.bloodGroup }),
      ...(config.parish && { parish: config.parish }),
      ...(config.label && { label: config.label }),
      ref: 'qr',
    });
    if (config.expiresIn) {
      const exp = new Date();
      exp.setDate(exp.getDate() + Number(config.expiresIn));
      params.set('exp', exp.toISOString().split('T')[0]);
    }
    return `${BASE_URL}/register-donor?${params.toString()}`;
  };

  const generateQR = async () => {
    const url = buildUrl();
    setQrUrl(url);
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        width: 400,
        margin: 2,
        color: { dark: '#1e1b4b', light: '#ffffff' },
        errorCorrectionLevel: 'H',
      });
      setQrDataUrl(dataUrl);
      setGenerated(true);
      toast.success('QR code generated!');
    } catch (err) {
      toast.error('Failed to generate QR: ' + err.message);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    const name = config.label || PRESETS[config.preset].label;
    link.download = `qr-registration-${name.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.href = qrDataUrl;
    link.click();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(qrUrl);
    toast.success('Registration link copied!');
  };

  const handleReset = () => {
    setGenerated(false);
    setQrDataUrl('');
    setQrUrl('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">QR Code Registration</h2>
          <p className="text-slate-500 text-sm mt-1">
            Generate a QR code that volunteers can scan to register as donors instantly.
          </p>
        </div>
        {generated && (
          <button onClick={handleReset}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium border border-indigo-200 rounded-lg px-3 py-1.5">
            ← New QR
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Config Panel ── */}
        {!generated ? (
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 shadow-sm">
            <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">Configure QR Code</h3>

            {/* Preset */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Registration Type</label>
              <div className="grid grid-cols-1 gap-2">
                {PRESETS.map((p, i) => (
                  <button key={i} onClick={() => setConfig(c => ({ ...c, preset: i }))}
                    className={`text-left px-4 py-3 rounded-lg border transition-all ${
                      config.preset === i
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}>
                    <p className="font-medium text-sm">{p.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Label */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Custom Label <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. OCYM Annual Camp 2025, St. Joseph's Parish"
                value={config.label}
                onChange={e => setConfig(c => ({ ...c, label: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </div>

            {/* Pre-fill fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pre-fill District</label>
                <select value={config.district}
                  onChange={e => setConfig(c => ({ ...c, district: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                  <option value="">— Any district —</option>
                  {DISTRICTS_KERALA.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pre-fill Blood Group</label>
                <select value={config.bloodGroup}
                  onChange={e => setConfig(c => ({ ...c, bloodGroup: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                  <option value="">— Any —</option>
                  {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>

            {/* Expiry */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Link Expires After</label>
              <select value={config.expiresIn}
                onChange={e => setConfig(c => ({ ...c, expiresIn: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                <option value="">Never expires</option>
                <option value="1">1 day</option>
                <option value="3">3 days</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">Expiry is informational — shown to users on the registration page.</p>
            </div>

            <button onClick={generateQR}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl text-sm transition-colors flex items-center justify-center gap-2 shadow-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 3.5V16M4 4h4v4H4V4zm12 0h4v4h-4V4zM4 16h4v4H4v-4z" />
              </svg>
              Generate QR Code
            </button>
          </div>
        ) : (
          /* ── Generated QR Panel ── */
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-semibold text-sm">QR Code Ready</span>
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1 font-medium">Type</p>
              <p className="text-sm text-slate-800 font-semibold">{PRESETS[config.preset].label}</p>
            </div>

            {config.label && (
              <div>
                <p className="text-xs text-slate-500 mb-1 font-medium">Label</p>
                <p className="text-sm text-slate-800">{config.label}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-sm">
              {config.district && (
                <div>
                  <p className="text-xs text-slate-500 mb-0.5 font-medium">District</p>
                  <p className="text-slate-700">{config.district}</p>
                </div>
              )}
              {config.bloodGroup && (
                <div>
                  <p className="text-xs text-slate-500 mb-0.5 font-medium">Blood Group</p>
                  <span className="inline-block bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded text-xs">{config.bloodGroup}</span>
                </div>
              )}
              {config.expiresIn && (
                <div>
                  <p className="text-xs text-slate-500 mb-0.5 font-medium">Expires</p>
                  <p className="text-slate-700">In {config.expiresIn} day{config.expiresIn !== '1' ? 's' : ''}</p>
                </div>
              )}
            </div>

            {/* Registration URL */}
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1 font-medium">Registration URL</p>
              <p className="text-xs text-slate-700 break-all font-mono">{qrUrl}</p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 pt-1">
              <button onClick={handleDownload}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download PNG
              </button>
              <button onClick={handleCopyLink}
                className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Link
              </button>
            </div>
          </div>
        )}

        {/* ── QR Preview Panel ── */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col items-center justify-center min-h-[400px]">
          {qrDataUrl ? (
            <div className="flex flex-col items-center gap-4 w-full">
              <div className="bg-white p-4 rounded-2xl shadow-md border border-slate-100 inline-block">
                <img src={qrDataUrl} alt="Registration QR Code" className="w-56 h-56 block" />
              </div>
              <div className="text-center space-y-1">
                <p className="font-bold text-slate-800 text-sm">
                  {config.label || PRESETS[config.preset].label}
                </p>
                <p className="text-xs text-slate-500">Scan to register as a blood donor</p>
                {config.expiresIn && (
                  <span className="inline-block text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2.5 py-0.5 font-medium">
                    Valid for {config.expiresIn} day{config.expiresIn !== '1' ? 's' : ''}
                  </span>
                )}
              </div>
              {/* Print hint */}
              <div className="mt-2 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3 text-center w-full">
                <p className="text-xs text-indigo-700 font-medium">💡 Print Tip</p>
                <p className="text-xs text-indigo-600 mt-0.5">
                  Download and print at A5 or A4 size for best scanning at events.
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center text-slate-400">
              <div className="w-24 h-24 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
                <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 3.5V16M4 4h4v4H4V4zm12 0h4v4h-4V4zM4 16h4v4H4v-4z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-slate-500">QR code will appear here</p>
              <p className="text-xs text-slate-400 mt-1">Configure options and click Generate</p>
            </div>
          )}
        </div>

      </div>

      {/* How it works */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-700 text-sm mb-3">How it works</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          {[
            { step: '1', title: 'Generate QR', desc: 'Admin creates a QR code for a specific event or location' },
            { step: '2', title: 'Display / Print', desc: 'Print the QR at the venue or share the link via WhatsApp' },
            { step: '3', title: 'Scan & Register', desc: 'Volunteers scan with their phone and fill the short registration form' },
            { step: '4', title: 'Auto-tagged', desc: 'New donors are tagged with the event/district automatically' },
          ].map(s => (
            <div key={s.step} className="flex gap-3 items-start">
              <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{s.step}</span>
              <div>
                <p className="text-sm font-semibold text-slate-800">{s.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
